import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useSupabaseChatStore } from '@/hooks/useSupabaseChatStore';
import SimpleChatHeader from '@/components/chat/SimpleChatHeader';
import SimpleChatMessageList from '@/components/chat/SimpleChatMessageList';
import SimpleChatInput from '@/components/chat/SimpleChatInput';
import ModelSelector from '@/components/chat/ModelSelector';
import ChatDialogs from '@/components/chat/ChatDialogs';
import { Attachment, Message, AIModel, Chat as ChatType } from '@/types';
import { toast, useToast } from "@/hooks/use-toast";
import { useSupabaseStore } from '@/store/SupabaseStore';
import { Badge } from "@/components/ui/badge";
import { Button } from '@/components/ui/button';

import { Switch } from '@/components/ui/switch';
import { isChatOSAccessEnabled } from '@/lib/osIntegration';
import { enableChatVFSAccess, disableChatVFSAccess } from '@/lib/osVFSBridge';
import { useAppStore } from '@/store';
import { useSubscription } from '@/hooks/useSubscription';
import { useAuth } from '@/contexts/AuthContext';
import { sendMessageToModel } from '@/services/ModelService';
import { v4 as uuidv4 } from 'uuid';
import { useCodeSnippets } from '@/hooks/supabase/useCodeSnippets';
import { supabase } from '@/integrations/supabase/client';
import { useIsMobile } from '@/hooks/use-mobile'; // Import useIsMobile
import TypingIndicator from '@/components/chat/TypingIndicator'; // Import TypingIndicator
import { useKnowledgeBaseSearch } from '@/hooks/chat/useKnowledgeBaseSearch'; // Add this import
import ReactMarkdown from 'react-markdown'; // Import ReactMarkdown
import remarkGfm from 'remark-gfm'; // Import remarkGfm
import { cleanMarkdownBold } from '@/lib/utils'; // Import cleanMarkdownBold
import { Tooltip, TooltipProvider, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Info } from 'lucide-react';

const isValidUUID = (uuid: string): boolean => {
  if (!uuid) return false;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
};

const FREE_PLAN_DAILY_LIMIT = 5;
const BASIC_PLAN_DAILY_LIMIT = 50;

const Chat = () => {
  const { chatId } = useParams<{ chatId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { addCodeSnippet, incrementChatViews, addMessage: addMessageToDb, addTokensUsed, models: availableModels } = useSupabaseStore();
  const { dismiss: dismissToast } = useToast();
  const viewCountTracked = useRef(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [pendingArena, setPendingArena] = useState(false);
  const isMobile = useIsMobile(); // Use the hook

  const { currentTier, loading: subscriptionLoading, fetchAndSetCurrentTier } = useSubscription();
  const { user: authUser } = useAuth();

  useEffect(() => {
    fetchAndSetCurrentTier();
  }, [authUser, fetchAndSetCurrentTier]);

  useEffect(() => {
    // Auto-create a new chat when no chatId is provided, using preferModel from URL when present
    if (!chatId) {
      const params = new URLSearchParams(location.search);
      const preferModel = params.get('preferModel');

      if (authUser && preferModel) {
        // Find preferred model or fallback to free/first available
        const preferred = availableModels.find(m => m.id === preferModel);
        const fallback = preferred || availableModels.find(m => m.tier === 'free') || availableModels[0];

        if (!fallback) {
          toast({
            title: "No models available",
            description: "Could not find a model to start the chat.",
            variant: "destructive"
          });
          return;
        }

        (async () => {
          const newChat = await useSupabaseStore.getState().addChat(authUser.id, 'New Chat', fallback.id);
          if (newChat?.id) {
            navigate(`/chat/${newChat.id}`);
          } else {
            toast({
              title: "Chat creation failed",
              description: "Could not create a new chat.",
              variant: "destructive"
            });
          }
        })();
      }
    }
  }, [chatId, location.search, authUser, availableModels, navigate]);

  const [messageCountToday, setMessageCountToday] = useState<number>(0);
  const [messageLimitLoading, setMessageLimitLoading] = useState(true);

  const getToday = () => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  };

  useEffect(() => {
    const fetchLimits = async () => {
      if (!authUser?.id) {
        setMessageLimitLoading(false);
        return;
      }
      setMessageLimitLoading(true);
      try {
        const today = getToday();
        const { data, error } = await supabase
          .from('user_daily_message_counts')
          .select('message_count')
          .eq('user_id', authUser.id)
          .eq('date', today)
          .maybeSingle();
        if (error) throw error;
        setMessageCountToday(data?.message_count || 0);
      } catch (err) {
        console.error("Failed to fetch daily message count:", err);
        setMessageCountToday(0);
      } finally {
        setMessageLimitLoading(false);
      }
    };
    fetchLimits();
  }, [authUser, currentTier]);

  const incrementMessageCount = async () => {
    const userId = authUser?.id;
    if (!userId) return;
    const today = getToday();
    await supabase.rpc('increment_user_daily_message_count', {
      user_id_input: userId,
      date_input: today
    });
    setMessageCountToday(prev => prev + 1);
  };

  const {
    messages,
    isLoading,
    chatTitle,
    modelName,
    selectedModelId,
    modelId2: arenaSecondModelId, // Use from store
    isUsingKnowledgeBase,
    sendMessage,
    handleTitleChange,
    handleDeleteChat,
    handleModelChange,
    handleModel2Change, // Use from store
    clearMessages,
    initializeChat,
    setMessages,
    isMemoryUpdating,
  } = useSupabaseChatStore();

  // Move currentChat definition here, before any useEffects that use it
  const { chats, updateChat } = useSupabaseStore();
  const currentChat = useMemo(() => {
    return chats.find(chat => chat.id === chatId);
  }, [chats, chatId]);
  const currentProjectId = currentChat?.project_id;
  const [projectKbId, setProjectKbId] = useState<string | null>(null);
  const [projectName, setProjectName] = useState<string | null>(null);

  // Ensure a KB exists for the project and set projectKbId
  useEffect(() => {
    const ensureKb = async () => {
      if (!currentProjectId || !authUser) {
        setProjectKbId(null);
        setProjectName(null);
        return;
      }
      // Resolve project name
      const { data: proj } = await supabase
        .from('projects')
        .select('id, name')
        .eq('id', currentProjectId)
        .maybeSingle();
      const name = proj?.name || 'Project';
      setProjectName(name);

      const kbName = `Project ${currentProjectId} KB`;
      const { data: kbExisting } = await supabase
        .from('knowledge_bases')
        .select('id')
        .eq('created_by', authUser.id)
        .eq('name', kbName)
        .maybeSingle();

      let kbId = kbExisting?.id || null;
      if (!kbId) {
        const { data: kbNew, error } = await supabase
          .from('knowledge_bases')
          .insert({
            name: kbName,
            description: `Knowledge base for project ${currentProjectId}`,
            organization_id: null,
            created_by: authUser.id,
            is_private: true,
          })
          .select('id')
          .single();
        if (!error) kbId = kbNew?.id || null;
      }
      setProjectKbId(kbId);
    };
    ensureKb();
  }, [currentProjectId, authUser?.id]);

  // Derive arenaMode directly from currentChat.type
  const arenaMode = currentChat?.type === 'arena'; // <--- Derived arenaMode

  // Move chatInGlobalStore definition here, before useEffects that use it
  const chatInGlobalStore = useSupabaseStore(state =>
    state.chats.find(c => c.id === chatId)
  );

  // Now all useEffects can safely use these variables
  useEffect(() => {
    if (chatInGlobalStore?.messages) {
      setMessages(chatInGlobalStore.messages);
    }
  }, [chatInGlobalStore?.messages, setMessages]);

  useEffect(() => {
    if (chatId && isValidUUID(chatId)) {
      initializeChat(chatId);
    } else {
      navigate('/');
    }
  }, [chatId, initializeChat, navigate]);

  // Add effect to reload messages when chat type changes to arena
  useEffect(() => {
    if (chatId && currentChat?.type === 'arena') {
      const { fetchChatMessages } = useSupabaseStore.getState();
      fetchChatMessages(chatId);
    }
  }, [chatId, currentChat?.type]);

  // Add effect to fix existing Arena messages with missing arena_order
  useEffect(() => {
    if (messages.length > 0 && arenaMode) { // Use derived arenaMode
      const arenaMessages = messages.filter(m => m.is_arena);
      const userMessagesWithMissingOrder = arenaMessages.filter(m => 
        m.role === 'user' && (m.arena_order === undefined || m.arena_order === null)
      );

      if (userMessagesWithMissingOrder.length > 0) {
        
        // Fix the messages in the local state
        const updatedMessages = messages.map(msg => {
          if (msg.role === 'user' && msg.is_arena && (msg.arena_order === undefined || msg.arena_order === null)) {
            return { ...msg, arena_order: 0 };
          }
          return msg;
        });
        
        setMessages(updatedMessages);
        
        // Also update in the global store
        useSupabaseStore.setState(state => ({
          chats: state.chats.map(chat =>
            chat.id === chatId
              ? {
                  ...chat,
                  messages: updatedMessages,
                }
              : chat
          ),
        }));
      }
    }
  }, [messages, arenaMode, chatId, setMessages]); // Use derived arenaMode

  // Add effect to restore arena message pairs when messages are loaded
  useEffect(() => {
    if (messages.length > 0 && arenaMode) { // Use derived arenaMode
      const arenaMessages = messages.filter(m => m.is_arena);
      const groups: Record<string, { userMessage?: Message; primaryResponse?: Message; secondaryResponse?: Message }> = {};
      
      // Add detailed logging for the grouping process
      for (const msg of arenaMessages) {

        if (!msg.arena_group_id) {
            continue;
        }
        if (!groups[msg.arena_group_id]) {
            groups[msg.arena_group_id] = {};
        }
        if (msg.arena_order === 0) {
          groups[msg.arena_group_id].userMessage = msg;
        } else if (msg.arena_order === 1) {
          groups[msg.arena_group_id].primaryResponse = msg;
        } else if (msg.arena_order === 2) {
          groups[msg.arena_group_id].secondaryResponse = msg;
        } else {
        }
      }
    
      
      const pairs = Object.values(groups)
        .filter(pair => {
            const hasUserMessage = !!pair.userMessage;
            return hasUserMessage;
        })
        .sort((a, b) => {
          const t1 = new Date(a.userMessage?.timestamp || 0).getTime();
          const t2 = new Date(b.userMessage?.timestamp || 0).getTime();
          return t1 - t2;
        });
      
      setArenaMessagePairs(pairs);
    } else if (!arenaMode) { // Use derived arenaMode
      console.log('[Chat.tsx] Not in Arena mode or no messages, clearing arenaMessagePairs.');
      setArenaMessagePairs([]);
    }
  }, [messages, arenaMode]); // Use derived arenaMode

  const [arenaMessagePairs, setArenaMessagePairs] = useState<
    Array<{
      userMessage: Message;
      primaryResponse?: Message;
      secondaryResponse?: Message;
    }>
  >([]);

  const [isCodeEditorOpen, setIsCodeEditorOpen] = useState(false);
  const [isCodeHistoryOpen, setIsCodeHistoryOpen] = useState(false);
  const [isCanvasOpen, setIsCanvasOpen] = useState(false);
  const [isChatHistoryOpen, setIsChatHistoryOpen] = useState(false);
  const codeSnippets = useRef<any[]>([]);
  const { fetchCodeSnippets } = useCodeSnippets(addMessageToDb);

  const fetchSnippets = useCallback(async () => {
    if (isCodeHistoryOpen || isCodeEditorOpen) {
      const snippets = await fetchCodeSnippets();
      const mapped = (snippets || []).map((snippet: any) => ({
        id: snippet.id,
        code: snippet.code,
        language: snippet.language,
        name: snippet.name,
        chatId: snippet.chat_id,
        timestamp: snippet.timestamp || snippet.created_at || new Date().toISOString(),
        userId: snippet.user_id,
        content: snippet.code,
      }));
      codeSnippets.current = mapped;
    }
  }, [isCodeHistoryOpen, isCodeEditorOpen, fetchCodeSnippets]);
  useEffect(() => { fetchSnippets(); }, [fetchSnippets]);

  useEffect(() => {
    // This useEffect now only handles the initial selection of the second model if not already set
    if (arenaMode && !arenaSecondModelId && availableModels.length > 1) {
      const alt = availableModels.find(m => m.id !== selectedModelId);
      if (alt) handleModel2Change(alt.id); // Use handler from store
    }
    if (!arenaMode) {
      setArenaMessagePairs([]);
    }
  }, [arenaMode, availableModels, selectedModelId, arenaSecondModelId, handleModel2Change]); // arenaMode is now derived

  const checkMessageLimit = () => {
    if (currentTier === 'free' && messageCountToday >= FREE_PLAN_DAILY_LIMIT) {
      toast({
        title: "Daily message limit reached",
        description: "You have reached your daily limit of 5 AI chat messages on the Free Plan. Upgrade for more.",
        variant: "destructive"
      });
      return true;
    }
    if (currentTier === 'basic' && messageCountToday >= BASIC_PLAN_DAILY_LIMIT) {
      toast({
        title: "Daily message limit reached",
        description: "You have reached your daily limit of 50 AI chat messages on the Basic Plan. Upgrade for unlimited access.",
        variant: "destructive"
      });
      return true;
    }
    return false;
  };

  const handleArenaSendMessage = async (content: string, attachments?: Attachment[]) => {
    if (!chatId || !authUser) return;
    if (!selectedModelId || !arenaSecondModelId) {
      toast({ title: "Select two models", description: "Please select both models for Arena mode.", variant: "destructive" });
      return;
    }
    if (checkMessageLimit()) return;
    setPendingArena(true);

    const arenaGroupId = uuidv4();

    const primaryModel = availableModels.find(m => m.id === selectedModelId);
    const secondaryModel = availableModels.find(m => m.id === arenaSecondModelId);

    if (!primaryModel || !secondaryModel) {
      toast({ title: "Model error", description: "Could not find both models.", variant: "destructive" });
      setPendingArena(false);
      return;
    }

    let userMessage: Message = {
      id: uuidv4(),
      content,
      role: 'user',
      sender: authUser.id,
      timestamp: new Date().toISOString(),
      attachments,
      chatId,
      is_arena: true,
      arena_group_id: arenaGroupId,
      arena_model: null,
      arena_order: 0,
    } as any;

    try {
      await addMessageToDb(chatId, {
        content,
        role: 'user',
        sender: authUser.id,
        attachments,
        is_arena: true,
        arena_group_id: arenaGroupId,
        arena_model: null,
        arena_order: 0,
      });

      await incrementMessageCount();

      const baseMessages: Message[] = [userMessage];

      const [primaryResult, secondaryResult] = await Promise.all([
        sendMessageToModel(baseMessages, primaryModel, availableModels, undefined, chatId),
        sendMessageToModel(baseMessages, secondaryModel, availableModels, undefined, chatId)
      ]);

      const totalTokens = (primaryResult.tokensUsed || 0) + (secondaryResult.tokensUsed || 0);
      if (totalTokens > 0) {
        await addTokensUsed(chatId, totalTokens);
      }

      const primaryResponse: Message = {
        id: uuidv4(),
        content: primaryResult.content,
        role: 'assistant',
        sender: 'ai',
        timestamp: new Date().toISOString(),
        model: primaryModel.id,
        chatId,
        is_arena: true,
        arena_group_id: arenaGroupId,
        arena_model: primaryModel.id,
        arena_order: 1,
      } as any;
      const secondaryResponse: Message = {
        id: uuidv4(),
        content: secondaryResult.content,
        role: 'assistant',
        sender: 'ai',
        timestamp: new Date().toISOString(),
        model: secondaryModel.id,
        chatId,
        is_arena: true,
        arena_group_id: arenaGroupId,
        arena_model: secondaryModel.id,
        arena_order: 2,
      } as any;

      await Promise.all([
        addMessageToDb(chatId, {
          content: primaryResult.content,
          role: 'assistant',
          sender: 'ai',
          model: primaryModel.id,
          is_arena: true,
          arena_group_id: arenaGroupId,
          arena_model: primaryModel.id,
          arena_order: 1,
        }),
        addMessageToDb(chatId, {
          content: secondaryResult.content,
          role: 'assistant',
          sender: 'ai',
          model: secondaryModel.id,
          is_arena: true,
          arena_group_id: arenaGroupId,
          arena_model: secondaryModel.id,
          arena_order: 2,
        }),
      ]);

      // Update the arena message pairs state
      setArenaMessagePairs(prev => [
        ...prev,
        { userMessage, primaryResponse, secondaryResponse }
      ]);
    } catch (err) {
      toast({ title: "Arena error", description: "Failed to get responses from both models or save to database.", variant: "destructive" });
    } finally {
      setPendingArena(false);
    }
  };

  const [messagesLoaded, setMessagesLoaded] = useState(false);
  useEffect(() => {
    if (chatId && messages.length > 0) {
      if (!messagesLoaded) setMessagesLoaded(true);
    }
  }, [messages, chatId, messagesLoaded]);

  useEffect(() => {
    if (chatId && isValidUUID(chatId) && !viewCountTracked.current) {
      incrementChatViews(chatId);
      viewCountTracked.current = true;
    }
  }, [chatId, incrementChatViews]);

  const persistedArenaPairs = useMemo(() => {
    const arenaMessages = messages.filter(m => m.is_arena);
    const groups: Record<string, { userMessage?: Message; primaryResponse?: Message; secondaryResponse?: Message }> = {};
    for (const msg of arenaMessages) {
      if (!msg.arena_group_id) continue;
      if (!groups[msg.arena_group_id]) groups[msg.arena_group_id] = {};
      if (msg.arena_order === 0) {
        groups[msg.arena_group_id].userMessage = msg;
      } else if (msg.arena_order === 1) {
        groups[msg.arena_group_id].primaryResponse = msg;
      } else if (msg.arena_order === 2) {
        groups[msg.arena_group_id].secondaryResponse = msg;
      }
    }
    return Object.values(groups)
      .filter(pair => pair.userMessage)
      .sort((a, b) => {
        const t1 = new Date(a.userMessage?.timestamp || 0).getTime();
        const t2 = new Date(b.userMessage?.timestamp || 0).getTime();
        return t1 - t2;
      });
  }, [messages]);

  const nonArenaMessages = useMemo(() => messages.filter(m => !m.is_arena), [messages]);

  const handleRegenerateResponse = async (messageId: string) => {
    const messageIndex = messages.findIndex(m => m.id === messageId);

    if (messageIndex <= 0) {
      toast({ title: "Error", description: "Could not find the original prompt to regenerate.", variant: "destructive" });
      return;
    }

    const aiMessage = messages[messageIndex];
    const userPrompt = messages[messageIndex - 1];

    if (aiMessage.role !== 'assistant' || userPrompt.role !== 'user') {
      toast({ title: "Invalid Action", description: "Only AI responses to a user's prompt can be regenerated.", variant: "destructive" });
      return;
    }

    const updatedMessages = messages.filter(m => m.id !== messageId);
    setMessages(updatedMessages);

    const { error: deleteError } = await supabase
      .from('messages')
      .delete()
      .eq('id', messageId);

    if (deleteError) {
      toast({ title: "Database Error", description: `Failed to delete the previous response: ${deleteError.message}`, variant: "destructive" });
      setMessages(messages);
      return;
    }

    await sendMessage(userPrompt.content, userPrompt.attachments);
  };

  const renderArenaMessages = () => (
    <div className="flex-1 overflow-y-auto px-2 py-4">
      <div className="flex flex-col gap-6">
        {[...persistedArenaPairs, ...arenaMessagePairs.filter(p => !persistedArenaPairs.some(pp => pp.userMessage?.id === p.userMessage?.id))]
          .map((pair, idx) => (
          <div key={pair.userMessage?.id || idx} className="border rounded-lg bg-muted/10 p-4">
            <div className="mb-2">
              <span className="font-semibold">You:</span>
              <div className="ml-2 text-sm">{pair.userMessage?.content}</div>
            </div>
            <div className="flex flex-col gap-4">
              <div className="border rounded bg-background p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline">{availableModels.find(m => m.id === selectedModelId)?.name || "Model 1"}</Badge>
                </div>
                <div className="text-sm whitespace-pre-line prose dark:prose-invert max-w-none">
                  {pair.primaryResponse?.content ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {cleanMarkdownBold(pair.primaryResponse.content)}
                    </ReactMarkdown>
                  ) : (
                    <span className="text-muted-foreground">Waiting for response...</span>
                  )}
                </div>
              </div>
              <div className="border rounded bg-background p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline">{availableModels.find(m => m.id === arenaSecondModelId)?.name || "Model 2"}</Badge>
                </div>
                <div className="text-sm whitespace-pre-line prose dark:prose-invert max-w-none">
                  {pair.secondaryResponse?.content ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {cleanMarkdownBold(pair.secondaryResponse.content)}
                    </ReactMarkdown>
                  ) : (
                    <span className="text-muted-foreground">Waiting for response...</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
        {pendingArena && (
          <div className="text-center text-muted-foreground py-4">Waiting for both models to respond...</div>
        )}
        <div ref={messagesEndRef} className="h-0" />
      </div>
    </div>
  );

  const renderArenaControls = () => (
    <div className="flex items-center gap-4 py-2 px-4 border-b bg-muted/30">
      <Badge variant="outline" className="bg-purple-100 text-purple-700 border-purple-300">
        Arena Mode
      </Badge>
      <span className="text-sm">Compare two models side-by-side</span>
      <div className="flex items-center gap-2 ml-auto">
        <span className="text-xs text-muted-foreground">Second Model:</span>
        <ModelSelector
          models={availableModels.filter(m => m.id !== selectedModelId)}
          selectedModelId={arenaSecondModelId || ''}
          onModelChange={handleModel2Change}
        />
      </div>
    </div>
  );

  const handleArenaInput = (content: string, attachments?: Attachment[]) => {
    handleArenaSendMessage(content, attachments);
  };

  const handleNormalInput = async (content: string, attachments?: Attachment[], kbId?: string | null, kbName?: string | null) => {
    if (checkMessageLimit()) return;
    // Prefer project KB when available
    const useKbId = projectKbId ?? kbId ?? null;
    const useKbName = projectName ?? kbName ?? null;
    await sendMessage(content, attachments, useKbId, useKbName, authUser);
    await incrementMessageCount();
  };

  const handleArenaToggle = () => {
    const isArenaEligible = currentTier === 'standard' || currentTier === 'enterprise';
    if (!isArenaEligible && !arenaMode) { // Allow turning off, but not on
      toast({
        title: "Arena Mode Unavailable",
        description: "Arena mode is only available for Standard and Enterprise users.",
        variant: "destructive"
      });
      return;
    }
    
    const newArenaMode = !arenaMode;

    if (newArenaMode && chatId && currentChat?.type !== 'arena') { // If enabling arena mode
      updateChat(chatId, { type: 'arena' });
      if (useAppStore.getState().saveHistory) {
        supabase.from('chats').update({ type: 'arena' }).eq('id', chatId).then(({ error }) => {
          if (error) console.error("Failed to update chat type to arena", error);
        });
      }
    } else if (!newArenaMode && chatId && currentChat?.type === 'arena') { // If disabling arena mode
      updateChat(chatId, { type: 'user-ai' });
      if (useAppStore.getState().saveHistory) {
        supabase.from('chats').update({ type: 'user-ai' }).eq('id', chatId).then(({ error }) => {
          if (error) console.error("Failed to update chat type from arena", error);
        });
      }
    }
  };

  const inputHandler = arenaMode ? handleArenaInput : handleNormalInput;

  const [osAccessEnabled, setOsAccessEnabled] = useState<boolean>(false);
  const [osAccessLoading, setOsAccessLoading] = useState<boolean>(false);

  useEffect(() => {
    const init = async () => {
      if (!chatId || !currentChat) return;
      try {
        const enabled = await isChatOSAccessEnabled(chatId, currentChat.title || 'Chat');
        setOsAccessEnabled(enabled);
      } catch (e) {
        console.warn('Failed to check document sync state:', e);
      }
    };
    init();
  }, [chatId, currentChat?.title]);

  const handleToggleOSAccess = async (value: boolean) => {
    if (!chatId || !currentChat) return;
    setOsAccessLoading(true);
    try {
      if (value) {
        await enableChatVFSAccess({ id: chatId, title: currentChat.title || chatTitle || 'Chat' } as any, messages);
        useSupabaseStore.getState().updateChat(chatId, { os_access_enabled: true });
        if (useAppStore.getState().saveHistory) {
          const { error } = await supabase
            .from('chats')
            .update({ os_access_enabled: true })
            .eq('id', chatId);
          if (error) console.error('Failed to set chats.os_access_enabled=true', error);
        }
        setOsAccessEnabled(true);
        toast({ title: 'Sync enabled', description: 'This chat will be mirrored into your desktop documents workspace.' });
      } else {
        await disableChatVFSAccess({ id: chatId, title: currentChat.title || chatTitle || 'Chat' } as any);
        useSupabaseStore.getState().updateChat(chatId, { os_access_enabled: false });
        if (useAppStore.getState().saveHistory) {
          const { error } = await supabase
            .from('chats')
            .update({ os_access_enabled: false })
            .eq('id', chatId);
          if (error) console.error('Failed to set chats.os_access_enabled=false', error);
        }
        setOsAccessEnabled(false);
        toast({ title: 'Sync disabled', description: 'The sync marker was removed. Existing mirrored files remain.' });
      }
    } catch (err: any) {
      toast({ title: 'Sync error', description: err.message || 'Failed to toggle sync.', variant: 'destructive' });
    } finally {
      setOsAccessLoading(false);
    }
  };

  // NEW: Trust Mode state
  const [trustEnabled, setTrustEnabled] = useState<boolean>(false);

  useEffect(() => {
    const saved = localStorage.getItem('trust_mode_enabled');
    setTrustEnabled(saved === 'true');
  }, []);

  const handleToggleTrust = (value: boolean) => {
    setTrustEnabled(value);
    localStorage.setItem('trust_mode_enabled', value ? 'true' : 'false');
    toast({
      title: value ? "Trust Mode enabled" : "Trust Mode disabled",
      description: value
        ? "Prompts will be checked for authorship, clarity, and presence; simulation blocked."
        : "Normal sandbox behavior restored.",
    });
  };

  const renderChatHeader = () => (
    <div className="flex-shrink-0 bg-background border-b">
      {/* This will stay at the top */}
      <SimpleChatHeader
        title={chatTitle}
        modelName={modelName}
        onTitleChange={handleTitleChange}
        onDeleteChat={handleDeleteChat}
        projectId={currentProjectId}
      />
      {arenaMode && renderArenaControls()}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center py-2 border-b px-4 gap-2">

        <div className="flex items-center gap-2">
          <ModelSelector
            models={availableModels}
            selectedModelId={selectedModelId || ''}
            onModelChange={handleModelSelectorChange}
          />
          {/* NEW: Trust Mode toggle with tooltip */}
          <div className="flex items-center gap-2 ml-2">
            <span className="text-sm">Trust Mode</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-6 w-6 p-0">
                    <Info className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-xs">
                    When ON, prompts are checked for authorship, clarity, and presence. Roleplay/simulation is blocked, sensitive info is redacted, and generation parameters are capped for safer, consistent results.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Switch checked={trustEnabled} onCheckedChange={handleToggleTrust} />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm">Enable Document Sync</span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-6 w-6 p-0">
                  <Info className="h-4 w-4 text-muted-foreground" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="text-xs">
                  Mirror this chat into your desktop documents workspace. Turning it off removes the sync marker but leaves any existing mirrored files in place.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Switch checked={osAccessEnabled} onCheckedChange={handleToggleOSAccess} disabled={osAccessLoading} />
        </div>
      </div>

    </div>
  );

  const handleInsertSnippetToChat = useCallback(
    async (snippet) => {
      if (!chatId) return;
  
      const attachment: Attachment = {
        id: Math.random().toString(36).substring(2, 11),
        type: 'code',
        content: snippet.code,
        name: snippet.name || `Code snippet (${snippet.language})`,
      };
  
      const message = `I've shared a code snippet in ${attachment.language}. Please analyze the attached code.`;
  
      await sendMessage(message, [attachment]);
  
      toast({
        title: "Code inserted into chat",
        description: "You can now ask the AI about this code.",
      });
      setIsCodeEditorOpen(false);
    },
    [chatId, sendMessage]
  );
  
  const handleModelSelectorChange = useCallback(
    (modelId: string) => {
      handleModelChange(modelId);
    },
    [handleModelChange]
  );

  useEffect(() => {
    if (chatInGlobalStore?.messages) {
      setMessages(chatInGlobalStore.messages);
    }
  }, [chatInGlobalStore?.messages, setMessages]);

  useEffect(() => {
    if (chatId && isValidUUID(chatId)) {
      initializeChat(chatId);
    } else {
      navigate('/');
    }
  }, [chatId, initializeChat, navigate]);

  // Add effect to reload messages when chat type changes to arena
  useEffect(() => {
    if (chatId && currentChat?.type === 'arena') {
      const { fetchChatMessages } = useSupabaseStore.getState();
      fetchChatMessages(chatId);
    }
  }, [chatId, currentChat?.type]);

  if (!chatId) {
    return (
      <div className="flex flex-col h-screen bg-background items-center justify-center">
        <div className="p-6 bg-card rounded-lg shadow-lg text-center max-w-md">
          <h2 className="text-xl font-semibold mb-4">Invalid Chat ID</h2>
          <p className="mb-4">The chat ID format is invalid. You will be redirected to the home page shortly.</p>
          <Button asChild className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90">
            <Link to="/">Back to Home</Link>
          </Button>
        </div>
      </div>
    );
  }

  // Calculate padding for messages and bottom offset for input based on mobile navigation
  const mobileNavHeight = 56; // Height of MobileNavigation is h-14 (14 * 4 = 56px)
  const inputHeightEstimate = 74; // Estimate input height (p-3 + min-h-[50px] textarea)
  const messagesPaddingBottom = inputHeightEstimate + 16; // 16px for some visual buffer
  const inputBottomOffset = 0; // Input should be at the very bottom

  return (
    <>
      <div className="flex flex-col h-full bg-background relative"> {/* Main container */}
        {/* Header - flex-none by default */}
        {renderChatHeader()}
        
        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {arenaMode
            ? renderArenaMessages()
            : (nonArenaMessages.length > 0 ? (
                <SimpleChatMessageList 
                  messages={nonArenaMessages} 
                  isLoading={isLoading}
                  messagesEndRef={messagesEndRef}
                  chatId={chatId}
                  hideCodeBlockIfAttachment
                  onRegenerate={handleRegenerateResponse}
                  isMemoryUpdating={isMemoryUpdating}
                />
              ) : isLoading ? (
                <div className="flex-1 flex items-center justify-center h-full">
                  <TypingIndicator />
                </div>
              ) : (
                <div className="h-full flex items-center justify-center text-center p-8 text-muted-foreground">
                  {/* Removed the h3 and p tags to hide the "Start a conversation" text */}
                </div>
              ))}
        </div>

        {/* Input Area - flex-none, naturally sits at bottom */}
        <div className="flex-none bg-background border-t p-3 z-10">
          <SimpleChatInput 
            onSendMessage={inputHandler}
            onClearChat={clearMessages}
            isLoading={isLoading || pendingArena || messageLimitLoading}
            onOpenCodeEditor={() => setIsCodeEditorOpen(true)}
            onOpenCanvas={() => setIsCanvasOpen(true)}
            onOpenCodeHistory={() => setIsCodeHistoryOpen(true)}
            onFileUpload={async (file) => {
              if (!chatId) return;
              if (file.size > 10 * 1024 * 1024) {
                toast({
                  title: "File too large",
                  description: "Maximum file size is 10 MB",
                });
                return;
              }
              const isImage = file.type.startsWith('image/');
              const isDocument = file.type === 'application/pdf' || 
                              file.type === 'text/plain' || 
                              file.type === 'text/csv' || 
                              file.name.endsWith('.pdf') || 
                              file.name.endsWith('.txt') || 
                              file.name.endsWith('.csv') || 
                              file.name.endsWith('.docx');
              if (!isImage && !isDocument) {
                toast({
                  title: "Unsupported file type",
                  description: "Please upload an image, PDF, TXT, CSV, or DOCX file",
                  variant: "destructive"
                });
                return;
              }
              const reader = new FileReader();
              reader.onload = async (event) => {
                if (event.target?.result) {
                  const attachment: Attachment = {
                    id: Math.random().toString(36).substring(2, 11),
                    type: isImage ? 'image' : 'file',
                    content: event.target.result as string,
                    name: file.name
                  };
                  await inputHandler(`Uploaded ${file.name}`, [attachment]);
                }
              };
              reader.readAsDataURL(file);
            }}
            onExportChat={() => {
              if (nonArenaMessages.length === 0) {
                toast({
                  title: "Nothing to export",
                  description: "The conversation is empty"
                });
                return;
              }
              const exportText = nonArenaMessages.map(msg => {
                const sender = msg.role === 'user' ? 'You' : 'AI';
                return `${sender}: ${msg.content}`;
              }).join('\n\n');
              const blob = new Blob([exportText], { type: 'text/plain' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `chat-export-${new Date().toISOString().split('T')[0]}.txt`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
              toast({
                title: "Chat exported",
                description: "The conversation has been exported as a text file"
              });
            }}
            chatId={chatId}
          />
        </div>
      </div>
      <ChatDialogs
        isCodeEditorOpen={isCodeEditorOpen}
        setIsCodeEditorOpen={setIsCodeEditorOpen}
        isCodeHistoryOpen={isCodeHistoryOpen}
        setIsCodeHistoryOpen={setIsCodeHistoryOpen}
        isCanvasOpen={isCanvasOpen}
        setIsCanvasOpen={setIsCanvasOpen}
        isChatHistoryOpen={isChatHistoryOpen}
        setIsChatHistoryOpen={setIsChatHistoryOpen}
        onSaveCode={async (code, language) => {
          if (!chatId) return;
          const attachment: Attachment = {
            id: Math.random().toString(36).substring(2, 11),
            type: 'code',
            content: code,
            name: `Code snippet (${language})`
          };
          const codeBlock = `\`\`\`${language}\n${code}\n\`\`\``;
          await sendMessage(
            `I've shared a code snippet in ${language}:\n${codeBlock}`,
            [attachment]
          );
          setIsCodeEditorOpen(false);
        }}
        onSelectCodeSnippet={async (snippetId) => {
          const snippet = codeSnippets.current.find(s => s.id === snippetId);
          if (snippet) {
            const attachment: Attachment = {
              id: Math.random().toString(36).substring(2, 11),
              type: 'code',
              content: snippet.code,
              name: snippet.name
            };
            const codeBlock = `\`\`\`${snippet.language || ''}\n${snippet.code}\n\`\`\``;
            await sendMessage(
              `I've shared a code snippet in ${snippet.language}:\n${codeBlock}`,
              [attachment]
            );
            setIsCodeHistoryOpen(false);
          }
        }}
        onSaveCanvas={async (imageData) => {
          const attachment: Attachment = {
            id: Math.random().toString(36).substring(2, 11),
            type: 'canvas',
            content: imageData,
            name: 'Canvas drawing'
          };
          await sendMessage("I've shared a drawing:", [attachment]);
          setIsCanvasOpen(false);
        }}
        onSelectChatHistory={() => {
          toast({ title: "Feature unavailable", description: "Adding messages from history is currently not supported."});
        }}
        codeSnippets={codeSnippets.current}
        onInsertSnippetToChat={handleInsertSnippetToChat}
      />
    </>
  );
};

export default Chat;